import { createApi } from "@reduxjs/toolkit/query/react";
import { appBaseQuery } from "../../services/baseQuery";
import { buildPagedListUrl } from "../../services/queryParams";

export const itemRoutingApi = createApi({
  reducerPath: "itemRoutingApi",
  baseQuery: appBaseQuery,
  tagTypes: ["ItemRouting", "RoutingParameter"],
  endpoints: (builder) => ({
    getRoutingParameters: builder.query({
      async queryFn(filters, api, extraOptions, baseQuery) {
        const parameters = [];
        // The existing API defaults to direct parameters; request both kinds.
        for (const direct_parameter of [true, false]) {
          let fetched = 0;
          for (let page = 1; ; page += 1) {
            const result = await baseQuery(buildPagedListUrl("parameter/", {
              ...filters, direct_parameter, page, limit: 1000,
            }));
            if (result.error) return { error: result.error };
            const response = result.data;
            if (response?.status === "failed" && response?.message !== "No Record Found") {
              return { error: { status: "CUSTOM_ERROR", error: response.message || "Unable to load parameters." } };
            }
            const batch = response?.data || [];
            if (!Array.isArray(batch)) return { error: { status: "CUSTOM_ERROR", error: "Invalid parameter response." } };
            parameters.push(...batch);
            fetched += batch.length;
            if (!batch.length || fetched >= (response?.meta_data?.total_records ?? fetched)) break;
          }
        }
        return { data: parameters.filter((parameter, index, rows) =>
          parameter.plant_id === filters.plant_id && parameter.item_id === filters.item_id && parameter.operation_id === filters.operation_id &&
          rows.findIndex((entry) => (entry.id || entry.parameter_id) === (parameter.id || parameter.parameter_id)) === index
        ) };
      },
      providesTags: ["RoutingParameter"],
    }),
    addRoutingParameter: builder.mutation({
      query: (parameter) => ({
        url: "parameter/",
        method: "POST",
        body: parameter,
      }),
      invalidatesTags: ["RoutingParameter"],
    }),
    updateRoutingParameter: builder.mutation({
      query: ({ uid, ...parameter }) => ({
        url: `parameter/${uid}`,
        method: "PUT",
        body: parameter,
      }),
      invalidatesTags: ["RoutingParameter"],
    }),
    getItemRoutings: builder.query({
      query: (params = {}) => buildPagedListUrl("item-routing/", params),
      providesTags: ["ItemRouting"],
    }),
    addItemRouting: builder.mutation({
      query: (newItemRouting) => ({
        url: "item-routing/",
        method: "POST",
        body: newItemRouting,
      }),
      invalidatesTags: ["ItemRouting"],
    }),
    addItemRoutingSeries: builder.mutation({
      query: (newItemRoutingSeries) => ({
        url: "item-routing/series",
        method: "POST",
        body: newItemRoutingSeries,
      }),
      invalidatesTags: ["ItemRouting"],
    }),
    updateItemRouting: builder.mutation({
      query: ({ uid, ...updatedItemRouting }) => ({
        url: `item-routing/${uid}`,
        method: "PUT",
        body: updatedItemRouting,
      }),
      invalidatesTags: ["ItemRouting"],
    }),
    updateItemRoutingSeries: builder.mutation({
      query: (updatedItemRoutingSeries) => ({
        url: "item-routing/series",
        method: "PUT",
        body: updatedItemRoutingSeries,
      }),
      invalidatesTags: ["ItemRouting"],
    }),
    deleteItemRouting: builder.mutation({
      query: (uid) => ({
        url: `item-routing/${uid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ItemRouting"],
    }),
  }),
});

export const {
  useGetRoutingParametersQuery,
  useAddRoutingParameterMutation,
  useUpdateRoutingParameterMutation,
  useGetItemRoutingsQuery,
  useLazyGetItemRoutingsQuery,
  useAddItemRoutingMutation,
  useAddItemRoutingSeriesMutation,
  useUpdateItemRoutingMutation,
  useUpdateItemRoutingSeriesMutation,
  useDeleteItemRoutingMutation,
} = itemRoutingApi;
